import React from "react";
import clsx from "clsx";
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  isSameDay,
  startOfMonth,
  startOfWeek,
  eachDayOfInterval,
  setMonth,
  setYear,
} from "date-fns";
import { es } from "date-fns/locale";
import { FormError } from "../../../../shared/components/forms/FormError";
import { useDateRangePicker } from "./useDateRangePicker";

interface DateRangePickerProps {
  id: string;
  label: string;
  required?: boolean;
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  maxRangeDays?: number;
  placeholder?: string;
  errorMessages?: string[];
}

const WEEKDAY_LABELS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
const MONTH_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  id,
  label,
  required,
  startDate,
  endDate,
  onChange,
  maxRangeDays = 32,
  placeholder = "Seleccionar rango de fechas",
  errorMessages = [],
}) => {
  const {
    activeMonth,
    displayValue,
    handleApply,
    handleCancel,
    handleDayKeyDown,
    handlePopoverKeyDown,
    handleSelectDate,
    handleTriggerKeyDown,
    isOpen,
    popoverRef,
    rangeError,
    setActiveMonth,
    setIsOpen,
    tempEnd,
    tempStart,
    toInputValue,
    triggerRef,
    wrapperRef,
  } = useDateRangePicker({
    startDate,
    endDate,
    onChange,
    maxRangeDays,
    placeholder,
  });
  const renderMonth = (monthDate: Date) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const cells: Date[] = eachDayOfInterval({ start: gridStart, end: gridEnd });

    return (
      <div className="flex flex-col gap-3" key={monthDate.toISOString()}>
        <div className="text-sm font-semibold text-gray-700 capitalize">
          {format(monthDate, "MMMM yyyy", { locale: es })}
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs text-gray-500">
          {WEEKDAY_LABELS.map((label) => (
            <span key={label} className="text-center">
              {label}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((date) => {
            const isOutsideMonth = !isSameMonth(date, monthDate);
            const isStart = !isOutsideMonth && tempStart ? isSameDay(date, tempStart) : false;
            const isEnd = !isOutsideMonth && tempEnd ? isSameDay(date, tempEnd) : false;
            const inRange =
              !isOutsideMonth && tempStart && tempEnd &&
              !isStart &&
              !isEnd &&
              isAfter(date, tempStart) &&
              isBefore(date, tempEnd);

            return (
              <button
                key={date.toISOString()}
                type="button"
                data-date={toInputValue(date)}
                onClick={() => handleSelectDate(date, monthDate)}
                onKeyDown={(event) => handleDayKeyDown(event, date)}
                className={clsx(
                  "h-8 w-8 rounded-full text-xs flex items-center justify-center transition-colors",
                  inRange && "bg-blue-100 text-blue-700",
                  (isStart || isEnd) && "bg-lifeguards-blue text-white",
                  !inRange && !isStart && !isEnd && "text-gray-700 hover:bg-gray-100",
                  isOutsideMonth && "text-gray-300 opacity-60"
                )}
                aria-pressed={isStart || isEnd}
                aria-selected={isStart || isEnd}
              >
                {format(date, "d")}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const errorId = errorMessages.length || rangeError ? `${id}-error` : undefined;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, index) => currentYear - 5 + index);

  return (
    <div className="flex flex-col gap-2" ref={wrapperRef}>
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-describedby={errorId}
        className={clsx(
          "w-full sm:w-auto min-w-[260px] rounded-md border px-3 py-2 text-left text-sm",
          "bg-white focus:outline-none focus:ring-2 focus:ring-lifeguards-blue",
          displayValue === placeholder ? "text-gray-500" : "text-gray-800",
          (errorMessages.length || rangeError) ? "border-red-400" : "border-gray-300"
        )}
      >
        {displayValue}
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-modal="false"
          className="relative z-20 mt-2 w-full max-w-[640px] rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
          ref={popoverRef}
          onKeyDown={handlePopoverKeyDown}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveMonth((prev) => addMonths(prev, -1))}
                className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                aria-label="Mes anterior"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setActiveMonth((prev) => addMonths(prev, 1))}
                className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                aria-label="Mes siguiente"
              >
                Siguiente
              </button>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={activeMonth.getMonth()}
                onChange={(event) => setActiveMonth(startOfMonth(setMonth(activeMonth, Number(event.target.value))))}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-lifeguards-blue"
                aria-label="Seleccionar mes"
              >
                {MONTH_LABELS.map((monthLabel, index) => (
                  <option key={monthLabel} value={index}>
                    {monthLabel}
                  </option>
                ))}
              </select>
              <select
                value={activeMonth.getFullYear()}
                onChange={(event) => setActiveMonth(startOfMonth(setYear(activeMonth, Number(event.target.value))))}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-lifeguards-blue"
                aria-label="Seleccionar año"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {renderMonth(activeMonth)}
            {renderMonth(addMonths(activeMonth, 1))}
          </div>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs text-gray-500">
              {tempStart && !tempEnd ? "Selecciona la fecha de fin" : ""}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={!tempStart || !tempEnd}
                className={clsx(
                  "rounded-md px-3 py-1.5 text-xs text-white transition-colors",
                  !tempStart || !tempEnd
                    ? "bg-gray-300"
                    : "bg-lifeguards-blue hover:bg-lifeguards-light-blue"
                )}
              >
                Seleccionar
              </button>
            </div>
          </div>
        </div>
      )}

      {rangeError && <FormError id={errorId} message={rangeError} />}
      {errorMessages.map((message) => (
        <FormError key={message} id={errorId} message={message} />
      ))}
    </div>
  );
};
